import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Switch } from "../../common/Switch";

export const CustomizationSection = ({ customizations, setCustomizations }) => {
  const [expanded, setExpanded] = useState(false);

  const handleInterfaceChange = (key, value) => {
    setCustomizations(prev => ({
      ...prev,
      interface: {
        ...prev.interface,
        [key]: value
      }
    }));
  };

  const handleDisplayChange = (key, value) => {
    setCustomizations(prev => ({
      ...prev,
      display: {
        ...prev.display,
        [key]: value
      }
    }));
  };

  const handleSidebarChange = (enabled) => {
    setCustomizations(prev => ({
      ...prev,
      sidebar: {
        ...prev.sidebar,
        enabled
      }
    }));
  };

  const handleSidebarTabChange = (tab, value) => {
    setCustomizations(prev => ({
      ...prev,
      sidebar: {
        ...prev.sidebar,
        tabs: {
          ...prev.sidebar.tabs,
          [tab]: value
        }
      }
    }));
  };

  const handleMapTypeChange = (type) => {
    setCustomizations(prev => ({
      ...prev,
      mapType: type
    }));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-white">Customization Options</h3>
        <button
          onClick={() => setExpanded(!expanded)}
          className="p-2 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
        >
          {expanded ? <ChevronUp className="text-white" /> : <ChevronDown className="text-white" />}
        </button>
      </div>

      {expanded && (
        <div className="space-y-6">
          {/* Map Type */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-300">Map Type</h4>
            <div className="grid grid-cols-2 gap-2">
              <button
                className={`p-2 rounded-lg ${
                  customizations.mapType === 'roadmap' 
                    ? 'bg-blue-500/20 border border-blue-500' 
                    : 'bg-gray-700 hover:bg-gray-600 border border-transparent'
                }`}
                onClick={() => handleMapTypeChange('roadmap')}
              >
                <span className="text-white">Road Map</span>
              </button>
              <button
                className={`p-2 rounded-lg ${
                  customizations.mapType === 'satellite' 
                    ? 'bg-blue-500/20 border border-blue-500' 
                    : 'bg-gray-700 hover:bg-gray-600 border border-transparent'
                }`}
                onClick={() => handleMapTypeChange('satellite')}
              >
                <span className="text-white">Satellite</span>
              </button>
            </div>
          </div>

          {/* Interface Options */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-300">Interface Options</h4>
            <div className="space-y-2">
              <Switch
                label="Show Search Bar"
                checked={customizations.interface.showSearch}
                onChange={() => handleInterfaceChange('showSearch', !customizations.interface.showSearch)}
              />
              <Switch
                label="Show Info Box"
                checked={customizations.interface.showInfoBox}
                onChange={() => handleInterfaceChange('showInfoBox', !customizations.interface.showInfoBox)}
              />
              <Switch
                label="Show Menu"
                checked={customizations.interface.showMenu}
                onChange={() => handleInterfaceChange('showMenu', !customizations.interface.showMenu)}
              />
              <Switch
                label="Show Location Button"
                checked={customizations.interface.showLocationButton}
                onChange={() => handleInterfaceChange('showLocationButton', !customizations.interface.showLocationButton)}
              />
              <Switch
                label="Show Drive To Button"
                checked={customizations.interface.showDriveToButton}
                onChange={() => handleInterfaceChange('showDriveToButton', !customizations.interface.showDriveToButton)}
              />
            </div>
          </div>

          {/* Display Options */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-300">Display Options</h4>
            <div className="space-y-2">
              <Switch
                label="Show Survey Name"
                checked={customizations.display.showSurveyName}
                onChange={() => handleDisplayChange('showSurveyName', !customizations.display.showSurveyName)}
              />
              <Switch
                label="Show Customer Name"
                checked={customizations.display.showCustomerName}
                onChange={() => handleDisplayChange('showCustomerName', !customizations.display.showCustomerName)}
              />
              <Switch
                label="Show Dates"
                checked={customizations.display.showDates}
                onChange={() => handleDisplayChange('showDates', !customizations.display.showDates)}
              />
              <Switch
                label="Show Vehicle Details"
                checked={customizations.display.showVehicleDetails}
                onChange={() => handleDisplayChange('showVehicleDetails', !customizations.display.showVehicleDetails)}
              />
              <Switch
                label="Show Additional Details"
                checked={customizations.display.showAdditionalDetails}
                onChange={() => handleDisplayChange('showAdditionalDetails', !customizations.display.showAdditionalDetails)}
              />
            </div>
          </div>

          {/* Sidebar Options */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-300">Sidebar Options</h4>
            <div className="space-y-2">
              <Switch
                label="Enable Sidebar"
                checked={customizations.sidebar.enabled}
                onChange={() => handleSidebarChange(!customizations.sidebar.enabled)}
              />
              
              {customizations.sidebar.enabled && (
                <div className="ml-4 mt-2 space-y-2">
                  <Switch
                    label="Basic Info Tab"
                    checked={customizations.sidebar.tabs.basic}
                    onChange={() => handleSidebarTabChange('basic', !customizations.sidebar.tabs.basic)}
                  />
                  <Switch
                    label="Routes Tab"
                    checked={customizations.sidebar.tabs.routes}
                    onChange={() => handleSidebarTabChange('routes', !customizations.sidebar.tabs.routes)}
                  />
                  <Switch
                    label="POIs Tab"
                    checked={customizations.sidebar.tabs.pois}
                    onChange={() => handleSidebarTabChange('pois', !customizations.sidebar.tabs.pois)}
                  />
                  <Switch
                    label="Share Tab"
                    checked={customizations.sidebar.tabs.share}
                    onChange={() => handleSidebarTabChange('share', !customizations.sidebar.tabs.share)}
                  />
                  <Switch
                    label="Files Tab"
                    checked={customizations.sidebar.tabs.files}
                    onChange={() => handleSidebarTabChange('files', !customizations.sidebar.tabs.files)}
                  />
                  <Switch
                    label="Settings Tab"
                    checked={customizations.sidebar.tabs.settings}
                    onChange={() => handleSidebarTabChange('settings', !customizations.sidebar.tabs.settings)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomizationSection;